<!-- eslint-disable no-undef -->
<script setup>
  // Imports
  import { ref, onMounted, watch } from 'vue';
  import LoadingAnimation3 from '../loadingIcons/LoadingAnimation3.vue';
  import InfoIcon from '../common/InfoIcon.vue';
  import { useModalBackdropClose } from '@/composables/useModalBackdropClose';

  // Definitions
  const props = defineProps({
    configDetails: {
      type: Object,
      default: () => ({})
    }
  });

  const emit = defineEmits(['changedDetails', 'close']);

  const loading = ref(false);
  const error = ref('');
  const serialNumber = ref(props.configDetails?.serial_number || '');
  const offset = ref(props.configDetails?.offset || 0);
  const updateFrequency = ref(props.configDetails?.update_frequency || 0);
  const reportInterval = ref(props.configDetails?.report_interval || 60);

  // Tooltip content for different sections
  const tooltipContent = {
    'Serial Number': 'Look for "Serial #" on the sensor label. Only enter the 5 digit number.',
    'Offset': 'This value is used to calibrate the sensor.\n\nExample: If the sensor is measuring 0.5psi when the actual value is 0psi, the offset would be -0.5.',
    'Update Frequency': 'How often (in seconds) the sensor sends data',
    'Report Interval': 'How often (in seconds) the XetaLynk™ gateway will send data.\n\n This value should not be lower than the update frequency.'
  };

  // Lifecycle hooks
  onMounted(async() => {
  });

  // Methods
  const handleSubmit = async() => {
    loading.value = true;
    error.value = '';

    let newDetails;

    if (props.configDetails) {
      newDetails = props.configDetails;

      newDetails.serial_number = serialNumber.value;
      newDetails.offset = offset.value;
      newDetails.update_frequency = updateFrequency.value;
      newDetails.report_interval = reportInterval.value;
    } else {
      newDetails = {
        serial_number: serialNumber.value,
        offset: offset.value,
        update_frequency: updateFrequency.value,
        report_interval: reportInterval.value
      };
    }

    // Emit the new config details and close the modal.
    emit('changedDetails', newDetails);
  }

  const close = () => {
    loading.value = false;
    emit('close');
  };

  const { onBackdropMousedown, onBackdropMouseup, onContentMousedown } = useModalBackdropClose(close);

  watch(reportInterval, (newVal) => {
    if (newVal < updateFrequency.value) {
      error.value = 'Report interval cannot be less than update frequency';
    } else {
      error.value = '';
    }
  });

  watch(updateFrequency, (newVal) => {
    if (newVal < reportInterval.value) {
      error.value = 'Update frequency cannot be less than report interval';
    } else {
      error.value = '';
    }
  });

</script>

<template>
  <div class="fixed top-0 left-0 right-0 bottom-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50" @mousedown.self="onBackdropMousedown" @mouseup.self="onBackdropMouseup">
    <div class="bg-white dark:bg-gray-800 p-8 rounded-xl w-[600px] max-h-[calc(100vh-50px)] overflow-y-auto shadow-2xl" @mousedown="onContentMousedown">
      <div class="h-[3px] w-full bg-mc-red rounded-full mb-5"></div>
      <h2 class="font-extrabold text-xl text-center mb-5 text-mc-ink dark:text-on-dark tracking-tight">
        CirrusSense TDWLB Wireless Pressure Transducer
      </h2>

      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Serial Number -->
        <div>
          <label for="tdwlb-serial" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
            Serial Number
            <InfoIcon :content="tooltipContent['Serial Number']" size="xs" />
          </label>
          <input id="tdwlb-serial" type="text" v-model="serialNumber" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
        </div>

        <!-- Offset -->
        <div>
          <label for="tdwlb-offset" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
            Offset
            <InfoIcon :content="tooltipContent['Offset']" size="xs" />
          </label>
          <input id="tdwlb-offset" type="number" v-model="offset" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
        </div>

        <!-- Update Frequency -->
        <div>
          <label for="tdwlb-update-frequency" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
            Update Frequency
            <InfoIcon :content="tooltipContent['Update Frequency']" size="xs" />
          </label>
          <input id="tdwlb-update-frequency" type="number" v-model="updateFrequency" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
        </div>

        <!-- Report Interval -->
        <div>
          <label for="tdwlb-report-interval" class="text-[10px] font-bold uppercase tracking-widest text-ink-muted dark:text-on-dark-muted mb-1.5 flex items-center gap-2">
            Report Interval
            <InfoIcon :content="tooltipContent['Report Interval']" size="xs" />
          </label>
          <input id="tdwlb-report-interval" type="number" v-model="reportInterval" class="w-full p-3 border border-mc-steel rounded-mc-sm bg-mc-white dark:bg-white/[0.05] text-mc-ink dark:text-on-dark focus-visible:outline-none focus-visible:border-mc-red focus-visible:ring-[3px] focus-visible:ring-mc-red/20" />
        </div>

        <!-- Error -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          <p class="text-sm">{{ error }}</p>
        </div>

        <!-- Save and close -->
        <div class="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            @click="close"
            class="px-5 py-2 text-xs rounded-full border border-mc-steel dark:border-white/20 text-ink-muted dark:text-on-dark-muted hover:bg-mc-steel/20 dark:hover:bg-white/10 transition-colors duration-160"
            >
            Cancel
          </button>

          <div class="flex items-center space-x-4">
            <LoadingAnimation3 v-if="loading" size="30px"/>
            <div v-else>
              <button
                type="submit"
                :disabled="loading || error !== ''"
                class="default-button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
